import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { PageLoader } from 'routes/PrivateRouter';
import { closeReconciliationURLs } from 'routes/close/closeURLs';
import { createRelativeTaskRoutes } from 'routes/close/createRelativeTaskRoutes';

import { PermissionGuard } from 'components/PermissionGuard';

import { useCustomerFeatureFlags } from 'hooks/useCustomerFlags';
import { useIsMaximorUser } from 'hooks/useIsMaximorUser';

import { featureFlags } from 'utils/config/featureFlags';

const { FF__CLOSE_DEMO_PAGES } = featureFlags;

const Reconciliations = lazy(() =>
  import('pages/period-close/reconciliations/Reconciliations').then((m) => ({
    default: m.Reconciliations,
  }))
);

const DemoReconciliations = lazy(() =>
  import('pages/period-close/reconciliations/demo/DemoReconciliations').then(
    (m) => ({
      default: m.DemoReconciliations,
    })
  )
);

const ReconciliationsV2 = lazy(() =>
  import('pages/period-close/reconciliation-v2/Reconciliations').then((m) => ({
    default: m.Reconciliations,
  }))
);

const ReconTaskDrawer = lazy(() =>
  import('pages/period-close/reconciliation-v2/ReconTaskDrawer').then((m) => ({
    default: m.ReconTaskDrawer,
  }))
);

const ReconciliationsRouteElement = () => {
  const {
    FF__IS_FACILIO_CUSTOMER,
    FF__IS_RENTLY_CUSTOMER,
    FF__IS_INVST_CUSTOMER,
  } = useCustomerFeatureFlags();
  const isMaximorUser = useIsMaximorUser();
  const isTargetCustomer =
    FF__IS_FACILIO_CUSTOMER || FF__IS_RENTLY_CUSTOMER || FF__IS_INVST_CUSTOMER;

  // Maximor users on target customers see V2 here so task drawer navigation works
  const showV1 = isTargetCustomer && !isMaximorUser;

  return (
    <PermissionGuard moduleKey="reconciliations">
      <Suspense fallback={<PageLoader />}>
        {showV1 ? (
          <Reconciliations />
        ) : FF__CLOSE_DEMO_PAGES ? (
          <DemoReconciliations />
        ) : (
          <ReconciliationsV2 />
        )}
      </Suspense>
    </PermissionGuard>
  );
};

export const CloseReconciliationsRoutes = {
  path: closeReconciliationURLs._baseRelativePath,
  children: [
    {
      index: true,
      element: <Navigate to={closeReconciliationURLs.list} replace />,
    },
    {
      path: ':view',
      element: <ReconciliationsRouteElement />,
      children: createRelativeTaskRoutes(ReconTaskDrawer),
    },
  ],
};

const ReconciliationsV1RouteElement = () => {
  const {
    FF__IS_FACILIO_CUSTOMER,
    FF__IS_RENTLY_CUSTOMER,
    FF__IS_INVST_CUSTOMER,
  } = useCustomerFeatureFlags();
  const isMaximorUser = useIsMaximorUser();
  const isTargetCustomer =
    FF__IS_FACILIO_CUSTOMER || FF__IS_RENTLY_CUSTOMER || FF__IS_INVST_CUSTOMER;

  if (!isTargetCustomer || !isMaximorUser) {
    return <Navigate to={closeReconciliationURLs.list} replace />;
  }

  return (
    <PermissionGuard moduleKey="reconciliations">
      <Suspense fallback={<PageLoader />}>
        <Reconciliations />
      </Suspense>
    </PermissionGuard>
  );
};

// V1 route for Maximor users on target customer workspaces to compare against V2
export const CloseReconciliationsV1Routes = {
  path: 'reconciliations-v1',
  children: [
    {
      index: true,
      element: <Navigate to="/close/reconciliations-v1/list" replace />,
    },
    {
      path: ':view',
      element: <ReconciliationsV1RouteElement />,
    },
  ],
};
